import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { httpsCallable } from 'firebase/functions';
import EditButton from '../../../components/ui/EditButton';
import { functions } from '../../../firebase-client';
import { EMPTY_VALUE_LABEL } from '../../../utils/formFieldPresentation';
import QualificationProfileField from '../qualifications/QualificationProfileField';
import { useQualificationRecords } from '../qualifications/useQualificationRecords';
const requestProfileUpdatesFn = httpsCallable(functions, 'requestProfileUpdates');

function hasValue(value) {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

function formatProfileValue(field, value) {
  if (!hasValue(value)) return EMPTY_VALUE_LABEL;
  if (field?.type === 'date') {
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString();
    }
  }
  return String(value);
}

export default function ProfileFieldsCard({
  config,
  isAdmin,
  teamId,
  memberId,
  memberProfileFields = {},
  profileUpdateRequested = false,
  profileUpdateRequestedAt = null,
  onEditConfig,
  onEditMine
}) {
  const [requesting, setRequesting] = useState(false);
  const [requestStatus, setRequestStatus] = useState('');
  const fields = useMemo(() => Array.isArray(config?.fields) ? config.fields : [], [config?.fields]);
  const enabled = config?.enabled !== false && fields.length > 0;
  const qualificationData = useQualificationRecords(teamId, memberId, enabled && fields.some(field => field.type === 'qualification'));
  const fieldHasValue = field => field.type === 'qualification' ? qualificationData.records.some(record => record.qualificationId === field.id && record.status) : hasValue(memberProfileFields?.[field.id]);
  const filledCount = (() => {
    if (!enabled) return 0;
    return fields.filter(fieldHasValue).length;
  })();
  const missingRequired = (() => {
    if (!enabled) return [];
    return fields.filter(field => field.required).filter(field => {
      return !fieldHasValue(field);
    });
  })();
  const requestedLabel = useMemo(() => {
    if (!profileUpdateRequestedAt?.toDate) return '';
    try {
      return profileUpdateRequestedAt.toDate().toLocaleDateString();
    } catch {
      return '';
    }
  }, [profileUpdateRequestedAt]);
  if (!enabled && !isAdmin) return null;
  const handleRequestUpdates = async () => {
    if (!teamId) return;
    setRequesting(true);
    setRequestStatus('');
    try {
      const result = await requestProfileUpdatesFn({
        teamId
      });
      const count = result?.data?.notifiedCount ?? 0;
      const missing = result?.data?.missingCount ?? 0;
      setRequestStatus(`Requested updates from ${count} member${count === 1 ? '' : 's'} (${missing} missing).`);
    } catch (err) {
      setRequestStatus(err?.message || 'Failed to request updates.');
    } finally {
      setRequesting(false);
    }
  };
  return <div className="team-card">
      <div className="card-header-with-icon" style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
        <span className="card-title">Profile Details</span>
        <div style={{
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center'
      }}>
          {enabled && <button type="button" className={isAdmin ? 'btn btn--secondary btn--xs' : 'btn btn--primary btn--xs'} onClick={onEditMine}>
              {isAdmin ? 'My Details' : profileUpdateRequested ? 'Update Details' : 'Edit Details'}
            </button>}
          {isAdmin && <button type="button" className="btn btn--secondary btn--xs" onClick={handleRequestUpdates} disabled={requesting || !enabled} title={enabled ? 'Request member updates' : 'Add fields to enable requests'}>
              {requesting ? 'Requesting...' : 'Request Updates'}
            </button>}
          {isAdmin && <EditButton section="profileFieldsConfig" onEdit={onEditConfig} label="Profile fields" />}
        </div>
      </div>
      <div className="card-body">
        {!enabled ? <p className="text-muted">No profile fields have been configured yet.</p> : <>
            {profileUpdateRequested && <div style={{
          marginBottom: '0.75rem',
          padding: '0.75rem 0.9rem',
          borderRadius: '12px',
          background: 'rgba(2, 132, 199, 0.08)',
          border: '1px solid rgba(2, 132, 199, 0.18)'
        }}>
                <div style={{
            fontWeight: 600,
            marginBottom: '0.2rem',
            color: '#0f172a'
          }}>
                  Profile update requested
                </div>
                <div className="text-muted">
                  Open your details and save to confirm the update{requestedLabel ? ` from ${requestedLabel}` : ''}.
                </div>
              </div>}
            <div style={{
          border: '1px solid rgba(15, 23, 42, 0.08)',
          borderRadius: '16px',
          padding: '1rem',
          background: 'rgba(248, 250, 252, 0.7)'
        }}>
              <div className="text-muted small" style={{
              marginBottom: '0.5rem'
            }}>
                Current profile
              </div>
              <p className="mb-2">
                {filledCount} of {fields.length} field{fields.length !== 1 ? 's' : ''} completed
                {fields.some(f => f.required) ? `, ${fields.filter(f => f.required).length} required` : ''}.
              </p>
              {missingRequired.length > 0 && <p className="text-muted" style={{
            marginBottom: '0.75rem'
          }}>
                  Missing required: {missingRequired.map(f => f.label).join(', ')}.
                </p>}
              <div style={{
              display: 'grid',
              gap: '0.75rem'
            }}>
                {fields.map(field => {
                const value = memberProfileFields?.[field.id];
                const present = hasValue(value);
                return <div key={field.id} className="profile-detail-field" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '1rem',
                      alignItems: 'baseline',
                      paddingBottom: '0.75rem',
                      borderBottom: '1px solid rgba(15, 23, 42, 0.08)'
                    }}>
                        <div>
                          <div style={{
                      fontWeight: 600
                    }}>
                            {field.label}
                            {field.required ? ' *' : ''}
                          </div>
                        </div>
                        <div className={present ? '' : 'text-muted'} style={{
                    textAlign: 'right'
                  }}>
                          {field.type === 'qualification' ? <QualificationProfileField readOnly teamId={teamId} memberId={memberId} field={field} record={qualificationData.records.find(record => record.qualificationId === field.id)} loaded={qualificationData.loaded} loadError={qualificationData.error} /> : formatProfileValue(field, value)}
                        </div>
                      </div>;
              })}
              </div>
            </div>
            {requestStatus && <p className="text-muted" style={{
          marginTop: '0.5rem'
        }}>{requestStatus}</p>}
          </>}
      </div>
    </div>;
}
ProfileFieldsCard.propTypes = {
  config: PropTypes.shape({
    enabled: PropTypes.bool,
    fields: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.string,
      required: PropTypes.bool,
      options: PropTypes.arrayOf(PropTypes.string)
    }))
  }),
  isAdmin: PropTypes.bool,
  teamId: PropTypes.string.isRequired,
  memberId: PropTypes.string,
  memberProfileFields: PropTypes.object,
  profileUpdateRequested: PropTypes.bool,
  profileUpdateRequestedAt: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onEditConfig: PropTypes.func,
  onEditMine: PropTypes.func
};
